import React from "react";
import { interpolate, spring } from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";

export function glow(c: string, strong = false): string {
  return strong
    ? `0 0 26px ${c}cc, 0 0 60px ${c}77, 0 0 120px ${c}33`
    : `0 0 16px ${c}99, 0 0 40px ${c}44`;
}

/* High-contrast backdrop with deep parallax */
export const Backdrop: React.FC<{ frame: number; accent: string; dark?: boolean }> = ({ frame, accent, dark = false }) => {
  const drift = Math.sin(frame * 0.005) * 8;
  return (
    <>
      {/* Deep radial wash */}
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(ellipse 80% 60% at 50% 45%, ${accent}${dark ? "10" : "18"} 0%, transparent 70%)`,
      }} />
      {/* Dense grid */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `linear-gradient(${accent}12 1px, transparent 1px), linear-gradient(90deg, ${accent}12 1px, transparent 1px)`,
        backgroundSize: "80px 80px",
        transform: `translateY(${drift}px)`,
      }} />
      {/* Parallax orbs */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {[
          { x: 220, y: 240, s: 560, c: accent,              sp: 0.005, ph: 0 },
          { x: 1680, y: 340, s: 480, c: COLORS.neonPurple,  sp: 0.004, ph: 2 },
          { x: 960, y: 880, s: 640, c: COLORS.neonBlue,     sp: 0.003, ph: 4 },
          { x: 420, y: 820, s: 380, c: COLORS.neonPink,     sp: 0.0035, ph: 1.2 },
          { x: 1520, y: 780, s: 420, c: COLORS.neonCyan,    sp: 0.0045, ph: 3.1 },
        ].map((o, i) => (
          <div key={i} style={{
            position: "absolute",
            left: o.x + Math.sin(frame * o.sp + o.ph) * 40 - o.s / 2,
            top: o.y + Math.cos(frame * o.sp * 0.7 + o.ph) * 28 - o.s / 2,
            width: o.s, height: o.s,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${o.c}0c 0%, transparent 70%)`,
          }} />
        ))}
      </div>
      {/* Particles */}
      {[...Array(18)].map((_, i) => {
        const px = 100 + ((i * 157) % 1800);
        const py = 60 + ((i * 97) % 960);
        const colors = [COLORS.neonPink, COLORS.neonBlue, COLORS.neonGreen, COLORS.neonCyan, COLORS.neonPurple, COLORS.neonYellow, COLORS.neonOrange];
        const c = colors[i % colors.length];
        const sz = 3 + (i % 4);
        return (
          <div key={`p-${i}`} style={{
            position: "absolute",
            left: px + Math.sin(frame * 0.02 + i * 1.3) * 22,
            top: py + Math.cos(frame * 0.015 + i * 1.7) * 18,
            width: sz, height: sz,
            borderRadius: "50%",
            background: c,
            boxShadow: `0 0 ${sz * 3}px ${c}cc, 0 0 ${sz * 6}px ${c}55`,
            opacity: 0.6 + Math.sin(frame * 0.03 + i) * 0.25,
            pointerEvents: "none",
          }} />
        );
      })}
    </>
  );
};

/* Slide number chip — top-left marker */
export const SlideChip: React.FC<{ n: number; total: number; color: string; appear: number }> = ({ n, total, color, appear }) => (
  <div style={{
    position: "absolute", top: 42, left: 60,
    opacity: appear,
    padding: "10px 22px",
    borderRadius: 30,
    background: `${color}18`,
    border: `1.5px solid ${color}aa`,
    fontSize: 18,
    fontFamily: FONTS.mono,
    color,
    fontWeight: 800,
    letterSpacing: 2.5,
    textShadow: glow(color),
    textTransform: "uppercase",
    zIndex: 30,
  }}>
    {String(n).padStart(2, "0")} / {String(total).padStart(2, "0")}
  </div>
);

/* Giant scene title */
export const SlideTitle: React.FC<{
  text: string;
  subtitle?: string;
  color: string;
  appear: number;
  size?: number;
}> = ({ text, subtitle, color, appear, size = 76 }) => (
  <div style={{
    position: "absolute", top: 110, left: 0, right: 0,
    textAlign: "center",
    opacity: appear,
    transform: `translateY(${(1 - appear) * -28}px)`,
  }}>
    <div style={{
      fontSize: size,
      fontFamily: FONTS.heading,
      fontWeight: 900,
      color: COLORS.textPrimary,
      textShadow: `0 0 40px ${color}dd, 0 0 80px ${color}66`,
      letterSpacing: -1.5,
      lineHeight: 1.1,
    }}>
      {text}
    </div>
    {subtitle && (
      <div style={{
        fontSize: 30,
        fontFamily: FONTS.body,
        color,
        marginTop: 14,
        opacity: Math.max(0, (appear - 0.3) / 0.7),
        letterSpacing: 1,
        fontWeight: 600,
        textShadow: glow(color),
      }}>
        {subtitle}
      </div>
    )}
  </div>
);

/* Reusable 3D card template.
 * Default: absolutely positioned at (x, y).
 * `inline`: position relative — for use inside flex/grid parents. */
export const Card3D: React.FC<{
  x?: number; y?: number; w: number; h: number;
  color: string;
  frame: number;
  delay: number;
  fps: number;
  rotY?: number;
  inline?: boolean;
  children: React.ReactNode;
}> = ({ x = 0, y = 0, w, h, color, frame, delay, fps, rotY = 0, inline = false, children }) => {
  const sp = spring({ frame: frame - delay, fps, config: SPRING.bouncy });
  const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const sc = interpolate(sp, [0, 1], [0.5, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const floatY = Math.sin(frame * 0.024 + delay * 0.05) * 6;

  const baseStyle: React.CSSProperties = inline
    ? {
        position: "relative",
        width: w,
        height: h,
        flexShrink: 0,
        opacity: op,
        transform: `translateY(${floatY}px) scale(${sc}) perspective(1200px) rotateY(${rotY + Math.sin(frame * 0.01) * 2}deg)`,
        transformStyle: "preserve-3d",
      }
    : {
        position: "absolute",
        left: x,
        top: y + floatY,
        width: w,
        height: h,
        opacity: op,
        transform: `scale(${sc}) perspective(1200px) rotateY(${rotY + Math.sin(frame * 0.01) * 2}deg)`,
        transformStyle: "preserve-3d",
      };

  return (
    <div style={baseStyle}>
      <div style={{
        width: "100%", height: "100%",
        background: `linear-gradient(140deg, ${color}22, ${COLORS.bgCard}ee)`,
        border: `2.5px solid ${color}`,
        borderRadius: 22,
        padding: 30,
        boxShadow: `0 0 40px ${color}55, 0 0 80px ${color}22, 0 20px 40px rgba(0,0,0,0.5)`,
      }}>
        {children}
      </div>
    </div>
  );
};

/* Bottom punchline */
export const Punchline: React.FC<{
  text: React.ReactNode;
  color: string;
  appear: number;
}> = ({ text, color, appear }) => (
  <div style={{
    position: "absolute", bottom: 70, left: 0, right: 0,
    textAlign: "center",
    opacity: appear,
  }}>
    <div style={{
      display: "inline-block",
      fontSize: 40,
      fontFamily: FONTS.heading,
      fontWeight: 800,
      color,
      padding: "18px 42px",
      borderRadius: 18,
      background: `${COLORS.bgCard}cc`,
      border: `2px solid ${color}`,
      textShadow: glow(color, true),
      letterSpacing: -0.3,
    }}>
      {text}
    </div>
  </div>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
